import { noop } from "lodash";
import { useTranslation } from "next-i18next";
import { ChangeEvent, FC, SyntheticEvent, useState } from "react";
import { Form } from "react-final-form";
import Checkbox from "@Components/forms/formFields/checkbox/Checkbox";
import FormTextField from "@Components/forms/formFields/textField/FormTextField";
import { FormMessage } from "@Components/forms/formMessage/FormMessage";
import CTFieldValidator, {
  confirmPassword,
  email,
  name,
  required,
  strongPassword,
  strongPasswordForCA,
} from "@Components/forms/helpers/FieldValidator";
import { formStateChangeHandler } from "@Components/forms/helpers/handlers";
import PasswordStrengthChecker from "@Components/password-strength-checker";
import { Button } from "@Components/ui";
import CmsTextEditorContent from "@Libs/contentful/components/cms-text-editor/CmsTextEditor";
import { ISignupFormSection } from "@Types/login-page";
import { isCanadianLocale } from "@Utils/locale";
import { submitFormActionSignup } from "@wff/components/forms/helpers/commonFormSubmission";
import { useSignup } from "@wff/hooks/useAuthHooks";
import {
  FORM_TYPES,
  useFormFieldValidation,
} from "@wff/hooks/useFormFieldValidation";
import { useLocaleRouter } from "@wff/hooks/useLocaleRouter";
import { setCustomerInfo } from "@wff/store/customerInfoSlice";
import { useAppDispatch, useAppSelector } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";
import classes from "../Login.module.scss";

export const PASSWORD_INPUT_ID = "form_field-password";
export const CONFIRM_PASSWORD_INPUT_ID = "form_field-password_confirmation";

interface Props {
  isCheckout?: boolean;
  signupFormSection?: ISignupFormSection;
}

const RegisterForm: FC<Props> = ({ isCheckout = false, signupFormSection }) => {
  const signUpFormMessage = signupFormSection?.signUpFormMessage;
  const signUpInputPlaceholderFirstName =
    signupFormSection?.signUpInputPlaceholderFirstName;
  const signUpInputPlaceholderLastName =
    signupFormSection?.signUpInputPlaceholderLastName;
  const signUpInputPlaceholderEmail =
    signupFormSection?.signUpInputPlaceholderEmail;
  const signUpInputPlaceholderPassword =
    signupFormSection?.signUpInputPlaceholderPassword;
  const signUpInputPlaceholderConfirmPassword =
    signupFormSection?.signUpInputPlaceholderConfirmPassword;
  const showNewsletterCheckbox = !!signupFormSection?.showNewsletterCheckbox;
  const newsletterCheckboxLabel = signupFormSection?.newsletterCheckboxLabel;
  const signUpButtonText = signupFormSection?.signUpButtonText;
  const signUpErrorMessagePasswordMismatch =
    signupFormSection?.signUpErrorMessagePasswordMismatch;
  const FirstNameInputLabel = signUpInputPlaceholderFirstName || "";
  const LastNameInputLabel = signUpInputPlaceholderLastName || "";
  const EmailInputLabel = signUpInputPlaceholderEmail || "";
  const PasswordInputLabel = signUpInputPlaceholderPassword || "";
  const PasswordConfirmInputLabel = signUpInputPlaceholderConfirmPassword || "";
  const signUpErrorHeading = signupFormSection?.signUpErrorHeading || "";

  const currentLocale = useAppSelector(localeSelector);
  const { push } = useLocaleRouter();
  const dispatch = useAppDispatch();
  const { signupState, setSignupState } = useSignup({
    acceptMarketing: showNewsletterCheckbox,
  });
  const { t } = useTranslation("common");
  const [password, setPassword] = useState({
    password: "",
    confirmedPassword: "",
  });
  const [fieldName, setFieldName] = useState("");
  const [showPasswordChecker, setShowPasswordChecker] = useState(false);
  const errorCodeTranslation = {
    WF_CUSTOMER_001: t("customer.form_errors.existing_account_html"),
    WF_CUSTOMER_002: t("customer.form_errors.blank_email"),
    WF_CUSTOMER_003: t("customer.form_errors.blank_password"),
    WF_CUSTOMER_005: t("customer.form_errors.blank_field"),
  };

  const validationTranslation = {
    allFieldsBlank: t("customer.form_errors.all_fields_blank"),
    firstNameBlank: t("customer.form_errors.invalid_first_name"),
    lastNameBlank: t("customer.form_errors.invalid_last_name"),
    emailBlank: t("customer.form_errors.blank_email"),
    emailNeedsContentAfterAtSymbol: t(
      "customer.form_errors.format_ends_with_at"
    ),
    passwordBlank: t("customer.form_errors.blank_password"),
    confirmPasswordBlank: t("customer.form_errors.blank_password"),
    passwordsDoNotMatch: t("customer.form_errors.password_mismatch"),
  };

  const actionOnFormSuccess = () => {
    const customerInfo = {
      firstName: signupState.firstName,
      lastName: signupState.lastName,
      email: signupState.email,
    };
    dispatch(setCustomerInfo(customerInfo));
    push("/account");
  };

  const {
    touched,
    setTouched,
    setFormError,
    formErrorMessages,
    setFormActionIsActive,
  } = useFormFieldValidation({
    type: FORM_TYPES.SIGNUP,
    touched: true, // this form does not require touching for validation
    formState: signupState,
    languageCode: currentLocale?.languageCode,
    errorCodeTranslation,
    validationTranslation,
    formAction: () => {
      signupState.email = signupState.email.trim();
      submitFormActionSignup({
        dispatch,
        payload: signupState,
        setFormError,
        errorCodeTranslation,
        actionOnFormSuccess,
      });
    },
  });

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement>,
    fieldStateKey: string
  ) => {
    formStateChangeHandler({
      key: fieldStateKey,
      value: e.target.value,
      formState: signupState,
      setTouched,
      formStateSetter: setSignupState,
    });
    if (fieldStateKey === "password" || fieldStateKey === "confirmedPassword") {
      setPassword((prevState) => ({
        ...prevState,
        [fieldStateKey]: e.target.value,
      }));
    }
  };

  /**
   * The function `handleFocus` checks if the active element is an input element and updates the state
   * variables `password` and `showPasswordChecker` based on the type and name of the input element.
   */
  const handleFocus = ({ target: { type, name, value } }: any) => {
    if (
      type === "password" ||
      name === "password" ||
      name === "passwordConfirm"
    ) {
      setPassword((prevState) => ({
        ...prevState,
        [name === "password" ? name : "confirmedPassword"]: value,
      }));
      setFieldName(name);
      setShowPasswordChecker(true);
      formErrorMessages.length = 0;
    } else {
      setShowPasswordChecker(false);
    }
  };

  const handleOnClickSubmit = async (
    e: SyntheticEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    setFormActionIsActive(true);
    setShowPasswordChecker(false);
  };
  const { locale } = useAppSelector((state) => state);
  return (
    <Form
      onSubmit={noop}
      render={({}) => (
        <>
          {
            /* This code is conditionally rendering the `<PasswordStrengthChecker />` 
              component based on the current locale's language code. */
            (currentLocale?.languageCode === "en-CA" ||
              currentLocale?.languageCode === "fr-CA") &&
              showPasswordChecker && (
                <PasswordStrengthChecker
                  value={password}
                  fieldName={fieldName}
                />
              )
          }
          <form onSubmit={noop} aria-label="checkout register form">
            <FormMessage
              message={signUpErrorHeading}
              type="error"
              errors={formErrorMessages}
            />
            <div className="customer-register__subtitle">
              <CmsTextEditorContent content={signUpFormMessage} />
            </div>
            <FormTextField
              aria-label="First Name"
              name="firstName"
              label={FirstNameInputLabel}
              data-testid="form_field-first_name"
              validate={CTFieldValidator(
                [required(t("customer.form_errors.blank_field")), name()],
                "First Name"
              )}
              outlined={!isCheckout}
              value={signupState.firstName}
              onChange={(e) => {
                handleInputChange(e, "firstName");
              }}
              onFocus={handleFocus}
            />
            <FormTextField
              aria-label="Last Name"
              name="lastName"
              label={LastNameInputLabel}
              data-testid="form_field-last_name"
              validate={CTFieldValidator(
                [required(t("customer.form_errors.blank_field")), name()],
                "Last Name"
              )}
              outlined={!isCheckout}
              value={signupState.lastName}
              onChange={(e) => {
                handleInputChange(e, "lastName");
              }}
              onFocus={handleFocus}
            />
            <FormTextField
              aria-label="Email"
              name="email"
              label={EmailInputLabel}
              data-testid="form_field-email"
              validate={CTFieldValidator(
                [
                  required(t("customer.form_errors.blank_email")),
                  email(t("customer.form_errors.invalid_email")),
                ],
                "Email"
              )}
              outlined={!isCheckout}
              value={signupState.email}
              onChange={(e) => {
                handleInputChange(e, "email");
              }}
              onFocus={handleFocus}
            />
            <FormTextField
              aria-label="Password"
              name="password"
              withPreview
              label={PasswordInputLabel}
              data-testid={PASSWORD_INPUT_ID}
              validate={CTFieldValidator(
                [
                  required(t("customer.form_errors.blank_password_field")),
                  isCanadianLocale(locale)
                    ? strongPasswordForCA(
                        t("customer.form_errors.blank_password")
                      )
                    : strongPassword(t("customer.form_errors.blank_password")),
                ],
                "Password"
              )}
              type="password"
              outlined={!isCheckout}
              value={signupState.password}
              onChange={(e) => {
                handleInputChange(e, "password");
              }}
              onFocus={handleFocus}
            />
            <FormTextField
              aria-label="Confirm Password"
              name="passwordConfirm"
              label={PasswordConfirmInputLabel}
              data-testid={CONFIRM_PASSWORD_INPUT_ID}
              validate={CTFieldValidator([
                confirmPassword(
                  signupState.password,
                  signUpErrorMessagePasswordMismatch
                ),
              ])}
              type="password"
              withPreview
              outlined={!isCheckout}
              value={signupState.confirmedPassword}
              onChange={(e) => {
                handleInputChange(e, "confirmedPassword");
              }}
              onFocus={handleFocus}
            />
            <Checkbox
              aria-label="Subscribe to Marketing"
              className={classes["form__subscribe-to-marketing"]}
              name="customer[accepts_marketing]"
              id="CreateCustomer-AcceptMarketing"
              label={<CmsTextEditorContent content={newsletterCheckboxLabel} />}
              isChecked={signupState.acceptMarketing}
              onChange={() => {
                formStateChangeHandler({
                  key: "acceptMarketing",
                  value: !signupState.acceptMarketing,
                  setTouched,
                  formState: signupState,
                  formStateSetter: setSignupState,
                });
              }}
            />
            <div className="form__actions">
              <Button
                className="action"
                data-testid="form_field-button-submit"
                onClick={handleOnClickSubmit}
                disabled={!touched}
                type="submit"
              >
                {signUpButtonText}
              </Button>
            </div>
          </form>
        </>
      )}
    />
  );
};

export default RegisterForm;
